import {
  u2000SumHonorariumDayTotals,
  type U2000HonorariumDayTotals,
} from '../../u2000Core/tools/U2000TimesheetHonorariumDayTotals';
import { u3129ClientBlockLabelFromProject } from '../../u3129Timesheet/tools/u3129BuildHonorariumLinesFromTasks';
import type { U3129FindProjectItem, U3129HonorariumProjectRef } from '../../u3129Timesheet/U3129Service';
import type { U3130HonorariumItem } from '../U3130TimesheetModel';
import { u3130ProjectWorkNatureFromRow } from './u3130HonorariumWorkNatureDisplay';

export const U3130_HONORARIUM_NO_PROJECT_KEY = '__sans_projet__';

export type U3130HonorariumLineRef = {
  index: number;
  row: U3130HonorariumItem;
};

/** Dossier (projet) de la grille honoraires en lecture : ligne client + lignes tâche. */
export type U3130HonorariumProjectGroup = {
  projectKey: string;
  projectId: string | null;
  /** Numéro de dossier brut (clé de tri par défaut) — vide si absent. */
  projetNumber: string;
  projectLabel: string;
  clientLabel: string;
  workNatureDisplay: string;
  lines: U3130HonorariumLineRef[];
  totals: U2000HonorariumDayTotals;
};

export function u3130CompareHonorariumText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function resolveProjectId(row: U3130HonorariumItem): string {
  return row.projectId?.trim() || row.project?.id?.trim() || '';
}

export function u3130ResolveHonorariumProjectKey(row: U3130HonorariumItem): string {
  return resolveProjectId(row) || U3130_HONORARIUM_NO_PROJECT_KEY;
}

export function u3130HonorariumClientLabel(row: U3130HonorariumItem | null | undefined): string {
  return u3129ClientBlockLabelFromProject(
    row?.project as U3129FindProjectItem | U3129HonorariumProjectRef | undefined,
  );
}

export function u3130HonorariumTaskLabel(row: U3130HonorariumItem): string {
  return row.task?.description?.trim() || row.taskType?.trim() || '';
}

export function u3130HonorariumSubprojectLabel(row: U3130HonorariumItem): string {
  const sp = row.subproject;
  if (!sp) return '';
  return [sp.subProjetNumber?.trim(), sp.subProjetName?.trim()].filter(Boolean).join(' ');
}

/** Ordre par défaut des tâches d’un dossier : sous-projet, puis libellé tâche, puis ordre de saisie. */
function compareLinesDefault(a: U3130HonorariumLineRef, b: U3130HonorariumLineRef): number {
  const sub = u3130CompareHonorariumText(u3130HonorariumSubprojectLabel(a.row), u3130HonorariumSubprojectLabel(b.row));
  if (sub !== 0) return sub;
  const task = u3130CompareHonorariumText(u3130HonorariumTaskLabel(a.row), u3130HonorariumTaskLabel(b.row));
  if (task !== 0) return task;
  return a.index - b.index;
}

/** Regroupe les lignes honoraires par dossier (ordre d’apparition ; le tri est appliqué par `u3130SortHonorariumProjectGroups`). */
export function u3130GroupHonorariumForDisplay(rows: U3130HonorariumItem[]): U3130HonorariumProjectGroup[] {
  const byProject = new Map<string, U3130HonorariumLineRef[]>();
  rows.forEach((row, index) => {
    const key = u3130ResolveHonorariumProjectKey(row);
    const list = byProject.get(key) ?? [];
    list.push({ index, row });
    byProject.set(key, list);
  });

  return [...byProject.entries()].map(([projectKey, lines]) => {
    const noProject = projectKey === U3130_HONORARIUM_NO_PROJECT_KEY;
    const sample = lines[0]?.row;
    const projetNumber = noProject ? '' : (sample?.project?.projetNumber?.trim() ?? '');
    return {
      projectKey,
      projectId: noProject ? null : projectKey,
      projetNumber,
      projectLabel: noProject ? '' : projetNumber || projectKey,
      clientLabel: noProject ? '' : u3130HonorariumClientLabel(sample),
      workNatureDisplay: noProject ? '' : u3130ProjectWorkNatureFromRow(sample),
      lines: [...lines].sort(compareLinesDefault),
      totals: u2000SumHonorariumDayTotals(lines.map((l) => l.row)),
    };
  });
}
